import { DEFAULT_REMOTE_SETTINGS } from '@mendikot/protocol';
import type {
  AnalyticsService,
  AuthService,
  AuthUser,
  CrashReporter,
  KeyValueStore,
  PerfService,
  PerfTrace,
  PushService,
  RemoteConfigService,
} from './services';

const noopTrace: PerfTrace = {
  putAttribute: () => undefined,
  putMetric: () => undefined,
  incrementMetric: () => undefined,
  stop: () => undefined,
};

export const noopAnalytics: AnalyticsService = {
  log: () => undefined,
  setUserId: () => undefined,
  setUserProperties: () => undefined,
  setConsent: () => undefined,
};

export const noopPerf: PerfService = { trace: () => noopTrace };

export const noopCrash: CrashReporter = {
  setUser: () => undefined,
  setContext: () => undefined,
  breadcrumb: () => undefined,
  captureError: (error) => console.error(error),
  captureMessage: () => undefined,
};

export const staticRemoteConfig: RemoteConfigService = {
  current: () => DEFAULT_REMOTE_SETTINGS,
  onChange: () => () => undefined,
  refresh: async () => undefined,
};

export const noopPush: PushService = {
  supported: async () => false,
  permission: () => 'unsupported',
  enable: async () => 'unsupported',
  onForeground: () => () => undefined,
};

export function memoryStore(): KeyValueStore {
  const map = new Map<string, string>();
  return {
    get: (key) => map.get(key) ?? null,
    set: (key, value) => void map.set(key, value),
    remove: (key) => void map.delete(key),
  };
}

/** Offline identity used when no auth backend is configured (practice only). */
export function localAuth(kv: KeyValueStore): AuthService {
  const KEY = 'mendikot.localUid';
  let uid = kv.get(KEY);
  if (!uid) {
    uid = `local-${Math.random().toString(36).slice(2, 12)}`;
    kv.set(KEY, uid);
  }
  const user: AuthUser = {
    uid,
    isAnonymous: true,
    displayName: null,
    email: null,
    emailVerified: false,
    providers: ['guest'],
  };
  const unavailable = async (): Promise<never> => {
    throw new Error('UNAVAILABLE');
  };
  return {
    available: false,
    current: () => user,
    onChange: (listener) => {
      listener(user);
      return () => undefined;
    },
    ensureSignedIn: async () => user,
    signInWithGoogle: unavailable,
    signInWithEmail: unavailable,
    signUpWithEmail: unavailable,
    sendPasswordReset: unavailable,
    signOut: async () => undefined,
    idToken: async () => null,
  };
}
